import React from 'react';
import { Settings, Activity, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { VoiceLayout } from './VoiceLayout';
import { INTEGRATIONS_AI } from '@/data/voiceAgents';
import { usePageTitle } from '@/lib/usePageTitle';
import { cn } from '@/lib/utils';

const STATUS_TONE = {
  Connected:       'border-success/40 bg-success/10 text-success',
  Mock:            'border-warning/40 bg-warning/10 text-warning',
  'Not Connected': 'border-muted bg-muted/40 text-muted-foreground',
  Error:           'border-destructive/40 bg-destructive/10 text-destructive',
};

export default function VoiceIntegrations() {
  usePageTitle('AI Voice Integrations');
  return (
    <VoiceLayout
      title="Settings & integrations"
      subtitle="Mock integration surface for telephony, payments, verification, knowledge sources and audit pipelines used by the AI voice agents."
    >
      <div className="rounded-md border border-warning/30 bg-warning/5 text-warning text-[12px] px-3 py-2">
        This is a front-end prototype. Integrations are simulated — no live data flows.
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {INTEGRATIONS_AI.map((it) => (
          <article key={it.id} className="surface-card p-4 flex flex-col gap-2.5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-semibold">{it.name}</p>
                <p className="text-[11px] text-muted-foreground">{it.vendor}</p>
              </div>
              <Badge variant="outline" className={cn('text-[10px]', STATUS_TONE[it.status])}>{it.status}</Badge>
            </div>

            <div className="grid grid-cols-3 gap-1.5 text-[11px]">
              <Field label="Latency" value={`${it.latency} ms`} />
              <Field label="Direction" value={it.direction} />
              <Field label="Last sync" value={it.lastSync} />
            </div>

            <div className="flex items-center gap-1.5 mt-auto pt-2 border-t border-border">
              <Button size="sm" variant="outline" className="h-7 text-[11px]"><Settings className="h-3 w-3 mr-1" aria-hidden="true" />Configure</Button>
              <Button size="sm" variant="ghost" className="h-7 text-[11px]"><FileText className="h-3 w-3 mr-1" aria-hidden="true" />View logs</Button>
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <Activity className="h-3 w-3" aria-hidden="true" />
                {it.status === 'Connected' ? 'Healthy' : it.status}
              </span>
            </div>
          </article>
        ))}
      </div>
    </VoiceLayout>
  );
}

const Field = ({ label, value }) => (
  <div className="rounded-md bg-muted/40 p-2">
    <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="text-[12px] font-mono-num">{value}</p>
  </div>
);
